import { isSearchAnswerRunOver, isSettledSearchAnswerStatus } from 'shared/panda/search-answer';
import type { LiveSearchAnswerRun, SearchAnswerSnapshot } from 'shared/panda/search-answer';

// Run, answer and handed-off conversation die together, so a stale snapshot can never re-seed a real chat thread.
interface SearchAnswerEntry {
  key: string;
  live: LiveSearchAnswerRun | null;
  snapshot: SearchAnswerSnapshot | null;
  conversationId: string | null;
}

let entry: SearchAnswerEntry | null = null;

export interface SearchAnswerCacheKeyParts {
  organizationId: string;
  term: string;
  /** Absent for a search opened by URL; the term alone then identifies the submission. */
  submitId?: string | null;
}

// Scoped to the organization so a same-term search never shows another org's answer.
export function searchAnswerKey({
  organizationId,
  term,
  submitId,
}: SearchAnswerCacheKeyParts): string {
  return JSON.stringify([organizationId, term.trim(), submitId ?? null]);
}

// A superseded run's late result is dropped, never resurrected over the newer search.
const mayStartEntry = (key: string) => !entry || (entry.key !== key && !entry.live);

// A hidden decision is kept too, so the card stays away without re-asking.
export function rememberSearchAnswer(key: string, snapshot: SearchAnswerSnapshot): void {
  if (!isSettledSearchAnswerStatus(snapshot.status)) return;
  if (entry?.key === key) entry.snapshot = snapshot;
  else if (mayStartEntry(key)) entry = { key, live: null, snapshot, conversationId: null };
}

export function recallSearchAnswer(key: string): SearchAnswerSnapshot | null {
  return entry?.key === key ? entry.snapshot : null;
}

// The conversation an answer was handed to, so a repeat "show more" after a remount reopens it.
export function rememberSearchAnswerConversation(key: string, conversationId: string): void {
  if (entry?.key === key) entry.conversationId = conversationId;
  else if (mayStartEntry(key)) entry = { key, live: null, snapshot: null, conversationId };
}

export function recallSearchAnswerConversation(key: string): string | null {
  return entry?.key === key ? entry.conversationId : null;
}

// The newest run for a submission: a card remounted meanwhile continues it in place. Kept past
// its end too — a run ending between a remount's render and its effect is adopted, not repeated.
export function keepLiveSearchAnswer(key: string, run: LiveSearchAnswerRun): void {
  if (entry?.key === key) {
    entry.live = run;
    return;
  }
  const superseded = entry?.live;
  if (
    superseded &&
    !isSearchAnswerRunOver(superseded.run.latest().status) &&
    !entry?.conversationId
  ) {
    superseded.run.abort();
  }
  entry = { key, live: run, snapshot: null, conversationId: null };
}

export function takeLiveSearchAnswer(key: string): LiveSearchAnswerRun | null {
  return entry?.key === key ? entry.live : null;
}
